import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import {
  Building,
  Users,
  Crown,
  TrendingUp,
  Search,
  Filter,
  Eye,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Zap,
} from 'lucide-react';
import { adminApi, AdminCompany } from '../services/api';

interface CompanyOversightSectionProps {
  isVisible: boolean;
  onClose: () => void;
  initialSearch?: string;
}

export default function CompanyOversightSection({
  isVisible,
  onClose,
  initialSearch = '',
}: CompanyOversightSectionProps) {
  const [companies, setCompanies] = useState<AdminCompany[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<AdminCompany[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<
    'all' | 'pro' | 'pro-paid' | 'pro-free' | 'free' | 'trial' | 'demo'
  >('all');
  const [selectedCompany, setSelectedCompany] = useState<AdminCompany | null>(null);
  useEffect(() => {
    if (isVisible) setSearchTerm(initialSearch);
  }, [isVisible, initialSearch]);

  // Pro toggle state
  const [proToggleModal, setProToggleModal] = useState<{
    company: AdminCompany;
    action: 'grant' | 'revoke';
  } | null>(null);
  const [processingCompanyId, setProcessingCompanyId] = useState<string | null>(null);

  // Fetch companies
  useEffect(() => {
    if (isVisible) {
      fetchCompanies();
    }
  }, [isVisible]);

  // Filter companies based on search and filter type
  useEffect(() => {
    let filtered = companies;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (company) =>
          company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (company.industry || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          company.owner?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          company.owner?.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (filterType === 'pro') {
      filtered = filtered.filter((company) => !company.isDemo && company.isPro);
    } else if (filterType === 'pro-paid') {
      filtered = filtered.filter(
        (company) =>
          !company.isDemo &&
          company.isPro &&
          (company.stripeSubscriptionId || company.stripeCustomerId)
      );
    } else if (filterType === 'pro-free') {
      filtered = filtered.filter(
        (company) =>
          !company.isDemo &&
          company.isPro &&
          !company.stripeSubscriptionId &&
          !company.stripeCustomerId
      );
    } else if (filterType === 'free') {
      filtered = filtered.filter(
        (company) => !company.isDemo && !company.isPro && !company.isInTrial
      );
    } else if (filterType === 'trial') {
      filtered = filtered.filter((company) => !company.isDemo && company.isInTrial);
    } else if (filterType === 'demo') {
      filtered = filtered.filter((company) => company.isDemo);
    }

    setFilteredCompanies(filtered);
  }, [companies, searchTerm, filterType]);

  const fetchCompanies = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await adminApi.getCompanies();
      if (response.success && response.data) {
        setCompanies(response.data);
      } else {
        setError('Failed to fetch companies');
      }
    } catch (err) {
      setError('Error fetching companies');
      console.error('Error fetching companies:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Handle pro features toggle
  const handleProToggle = async (company: AdminCompany, enablePro: boolean, reason: string) => {
    if (company.isDemo) return;
    try {
      setProcessingCompanyId(company.id);

      const response = await adminApi.toggleCompanyProFeatures(company.id, enablePro, reason);

      if (response.success) {
        // Update the company in the local state
        setCompanies((prev) =>
          prev.map((c) =>
            c.id === company.id
              ? { ...c, isPro: enablePro, subscriptionTier: enablePro ? 'pro' : 'free' }
              : c
          )
        );

        setProToggleModal(null);
      } else {
        alert('Failed to update company: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error toggling pro features:', error);
      alert('Error updating company pro features');
    } finally {
      setProcessingCompanyId(null);
    }
  };

  const getSubscriptionBadge = (company: AdminCompany) => {
    if (company.isDemo) return <span className="adviser-badge">Private demo</span>;
    if (company.isInTrial) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <Clock className="h-3 w-3 mr-1" />
          Trial
        </span>
      );
    }
    if (company.isPro) {
      // Check if it's a paid subscription (has Stripe subscription ID) or manually granted
      const isPaidPro = company.stripeSubscriptionId || company.stripeCustomerId;

      if (isPaidPro) {
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            <Crown className="h-3 w-3 mr-1" />
            Pro (Paid)
          </span>
        );
      } else {
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
            <Zap className="h-3 w-3 mr-1" />
            Pro (Free)
          </span>
        );
      }
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Free
      </span>
    );
  };

  const getBillingStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      past_due: { color: 'bg-red-100 text-red-800', icon: AlertTriangle },
      canceled: { color: 'bg-gray-100 text-gray-800', icon: Clock },
      trialing: { color: 'bg-blue-100 text-blue-800', icon: Clock },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    const IconComponent = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
      >
        <IconComponent className="h-3 w-3 mr-1" />
        {status.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
      </span>
    );
  };

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-7xl h-full max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Building className="h-6 w-6 text-green-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Company Oversight</h2>
              <p className="text-sm text-gray-600">Monitor all companies on the platform</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            ✕
          </button>
        </div>

        {/* Controls */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search companies by name, industry, or owner..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Filter */}
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={filterType}
                onChange={(e) =>
                  setFilterType(
                    e.target.value as
                      | 'all'
                      | 'pro'
                      | 'pro-paid'
                      | 'pro-free'
                      | 'free'
                      | 'trial'
                      | 'demo'
                  )
                }
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Companies</option>
                <option value="demo">Private demos</option>
                <option value="pro">All Pro</option>
                <option value="pro-paid">Pro (Paid)</option>
                <option value="pro-free">Pro (Free)</option>
                <option value="trial">Trial Users</option>
                <option value="free">Free Users</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex items-center space-x-6 text-sm text-gray-600">
            <span>Total Companies: {companies.length}</span>
            <span>
              Pro (Paid):{' '}
              {
                companies.filter(
                  (c) => !c.isDemo && c.isPro && (c.stripeSubscriptionId || c.stripeCustomerId)
                ).length
              }
            </span>
            <span>
              Pro (Free):{' '}
              {
                companies.filter(
                  (c) => !c.isDemo && c.isPro && !c.stripeSubscriptionId && !c.stripeCustomerId
                ).length
              }
            </span>
            <span>Trial: {companies.filter((c) => !c.isDemo && c.isInTrial).length}</span>
            <span>Demos: {companies.filter((c) => c.isDemo).length}</span>
            <span>Showing: {filteredCompanies.length}</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading companies...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">{error}</p>
                <button
                  onClick={fetchCompanies}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-auto h-full">
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 p-6">
                {filteredCompanies.map((company) => (
                  <motion.div
                    key={company.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    {/* Company Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                          {company.logo ? (
                            <img
                              src={company.logo}
                              alt={company.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Building className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">{company.name}</h3>
                          <p className="text-sm text-gray-500 truncate">{company.industry}</p>
                        </div>
                      </div>
                      {getSubscriptionBadge(company)}
                    </div>

                    {/* Owner Info */}
                    {company.owner && (
                      <div className="flex items-center space-x-2 mb-3 p-2 bg-gray-50 rounded">
                        <Crown className="h-4 w-4 text-yellow-600" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {company.owner.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{company.owner.email}</p>
                        </div>
                      </div>
                    )}

                    {/* Company Stats */}
                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                      <div className="flex items-center justify-between">
                        <span>Members:</span>
                        <span className="flex items-center">
                          <Users className="h-3 w-3 mr-1" />
                          {company.memberCount}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Size:</span>
                        <span className="capitalize">{company.size}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Created:</span>
                        <span>{formatDate(company.createdAt)}</span>
                      </div>
                      {company.recentActivityCount > 0 && (
                        <div className="flex items-center justify-between">
                          <span>Recent Activity:</span>
                          <span className="flex items-center text-green-600">
                            <TrendingUp className="h-3 w-3 mr-1" />
                            {company.recentActivityCount} new members
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Subscription Details */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                          {company.isDemo ? 'Entitlement:' : 'Billing Status:'}
                        </span>
                        {company.isDemo ? (
                          <span>Demo access</span>
                        ) : (
                          getBillingStatusBadge(company.billingStatus)
                        )}
                      </div>

                      {company.isPro && !company.isDemo && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Pro Users:</span>
                          <span className="font-medium">{company.proUserCount}</span>
                        </div>
                      )}

                      {!company.isDemo && company.isInTrial && company.trialEndDate && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Trial Ends:</span>
                          <span className="text-orange-600 font-medium">
                            {formatDate(company.trialEndDate)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="space-y-2">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setSelectedCompany(company)}
                          className="flex-1 bg-green-600 text-white py-2 px-3 rounded text-sm hover:bg-green-700 transition-colors flex items-center justify-center"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Details
                        </button>
                        <button
                          onClick={() => window.open(`/company/${company.id}`, '_blank')}
                          className="bg-gray-100 text-gray-700 py-2 px-3 rounded text-sm hover:bg-gray-200 transition-colors"
                        >
                          <Building className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Pro Features Toggle */}
                      <button
                        onClick={() =>
                          setProToggleModal({
                            company,
                            action: company.isPro ? 'revoke' : 'grant',
                          })
                        }
                        disabled={Boolean(company.isDemo) || processingCompanyId === company.id}
                        title={
                          company.isDemo ? 'Private demos have no paid subscription' : undefined
                        }
                        className={`w-full py-2 px-3 rounded text-sm font-medium transition-colors flex items-center justify-center ${
                          company.isPro
                            ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                            : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                        } ${company.isDemo || processingCompanyId === company.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {processingCompanyId === company.id ? (
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                        ) : (
                          <Zap className="h-4 w-4 mr-1" />
                        )}
                        {company.isDemo
                          ? 'Demo entitlement'
                          : company.isPro
                            ? 'Revoke Pro'
                            : 'Grant Pro'}
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>

              {filteredCompanies.length === 0 && !isLoading && (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center text-gray-500">
                    <Building className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No companies found</p>
                    {searchTerm && (
                      <p className="text-sm mt-1">Try adjusting your search or filters</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>

      {/* Company Detail Modal */}
      {selectedCompany && (
        <CompanyDetailModal company={selectedCompany} onClose={() => setSelectedCompany(null)} />
      )}

      {/* Pro Toggle Confirmation Modal */}
      {proToggleModal && (
        <ProToggleModal
          company={proToggleModal.company}
          action={proToggleModal.action}
          onConfirm={handleProToggle}
          onCancel={() => setProToggleModal(null)}
          isProcessing={processingCompanyId === proToggleModal.company.id}
        />
      )}
    </motion.div>
  );
}

// Company Detail Modal Component
interface CompanyDetailModalProps {
  company: AdminCompany;
  onClose: () => void;
}

function CompanyDetailModal({ company, onClose }: CompanyDetailModalProps) {
  const [details, setDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await adminApi.getCompanyDetails(company.id);
        if (response.success && response.data) {
          setDetails(response.data);
        }
      } catch (err) {
        console.error('Error fetching company details:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [company.id]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-auto"
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                {company.logo ? (
                  <img
                    src={company.logo}
                    alt={company.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Building className="h-6 w-6 text-gray-400" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{company.name}</h2>
                <p className="text-sm text-gray-600">
                  {company.industry} • {company.size}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors text-xl"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : details ? (
            <div className="space-y-6">
              {/* Company Info */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Company Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Created:</span>
                    <span className="ml-2 font-medium">
                      {new Date(company.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Members:</span>
                    <span className="ml-2 font-medium">{company.memberCount}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Subscription:</span>
                    <span className="ml-2 font-medium capitalize">{company.subscriptionTier}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Billing Status:</span>
                    <span className="ml-2 font-medium capitalize">{company.billingStatus}</span>
                  </div>
                </div>
              </div>

              {/* Members */}
              {details.members && details.members.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Team Members</h3>
                  <div className="space-y-2">
                    {details.members.map((member: any) => (
                      <div
                        key={member.userId}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                            {member.photo ? (
                              <img
                                src={member.photo}
                                alt={member.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Users className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{member.name}</p>
                            <p className="text-sm text-gray-500">{member.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              member.role === 'owner'
                                ? 'bg-yellow-100 text-yellow-800'
                                : member.role === 'admin'
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {member.role}
                          </span>
                          {member.superUser && (
                            <div className="text-xs text-red-600 mt-1 flex items-center">
                              <Crown className="h-3 w-3 mr-1" />
                              Super User
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pending Invitations */}
              {details.pendingInvitations && details.pendingInvitations.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Pending Invitations</h3>
                  <div className="space-y-2">
                    {details.pendingInvitations.map((invitation: any) => (
                      <div
                        key={invitation.id}
                        className="flex items-center justify-between p-3 bg-yellow-50 rounded border border-yellow-200"
                      >
                        <div>
                          <p className="font-medium text-gray-900">{invitation.invitedEmail}</p>
                          <p className="text-sm text-gray-500">
                            Invited {new Date(invitation.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          {invitation.role}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 h-32 flex items-center justify-center">
              Failed to load company details
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// Pro Toggle Confirmation Modal Component
interface ProToggleModalProps {
  company: AdminCompany;
  action: 'grant' | 'revoke';
  onConfirm: (company: AdminCompany, enablePro: boolean, reason: string) => Promise<void>;
  onCancel: () => void;
  isProcessing: boolean;
}

function ProToggleModal({
  company,
  action,
  onConfirm,
  onCancel,
  isProcessing,
}: ProToggleModalProps) {
  const [reason, setReason] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onConfirm(company, action === 'grant', reason);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
      >
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  action === 'grant' ? 'bg-purple-100' : 'bg-orange-100'
                }`}
              >
                <Zap
                  className={`h-5 w-5 ${
                    action === 'grant' ? 'text-purple-600' : 'text-orange-600'
                  }`}
                />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {action === 'grant' ? 'Grant Pro Features' : 'Revoke Pro Features'}
                </h3>
                <p className="text-sm text-gray-600">{company.name}</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div
              className={`p-4 rounded-lg border ${
                action === 'grant'
                  ? 'bg-purple-50 border-purple-200'
                  : 'bg-orange-50 border-orange-200'
              }`}
            >
              <h4 className="font-medium text-gray-900 mb-2">
                {action === 'grant' ? 'This will grant:' : 'This will revoke:'}
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Unlimited users</li>
                <li>• Unlimited strategies</li>
                <li>• Advanced AI features</li>
                <li>• Premium support access</li>
              </ul>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason (optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for this action (for audit logs)..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                rows={3}
              />
            </div>

            {action === 'revoke' && company.isPro && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">Warning:</p>
                    <p>Revoking pro features will limit this company to free tier restrictions.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-6 border-t border-gray-200 flex space-x-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={isProcessing}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing}
              className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center ${
                action === 'grant'
                  ? 'bg-purple-600 hover:bg-purple-700'
                  : 'bg-orange-600 hover:bg-orange-700'
              }`}
            >
              {isProcessing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  {action === 'grant' ? 'Grant Pro Features' : 'Revoke Pro Features'}
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
